import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalLink, Check, Loader2, CheckCircle, XCircle, Eye, EyeOff, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "./ui/alert";

interface ApiKeyCardProps {
  title: string;
    description: string;
    icon: any;
    required?: boolean;
    fields: Array<{
      key: string;
      label: string;
      placeholder: string;
      type?: string;
      validation?: string;
    }>;
    docUrl: string;
    serviceKey: string;
    testAction?: () => void;
    forceStatus?: 'idle' | 'saving' | 'valid' | 'invalid';
    hideFields?: boolean;
    showClearButton?: boolean;
    onClear?: () => void;
    validationStatus?: any;
    showSecrets?: {[key: string]: boolean};
    formData?:{
        openaiApiKey: string;
        xaiApiKey: string;
        twilioSid: string;
        twilioAuthToken: string;
        elevenlabsApiKey: string;
        vapiApiKey: string;
        webhookUrl: string;
        zapierWebhook: string;
        makeWebhook: string;
    };
    handleInputChange?: (key: string, value: string) => void;
    toggleSecretVisibility?: (key: string) => void;
    handleSaveService?: (serviceKey: string) => void;
}

export function ApiKeyCard({
    title,
    description,
    icon: Icon,
    required = false,
    fields,
    docUrl,
    serviceKey,
    testAction,
    forceStatus,
    hideFields = false,
    showClearButton = false,
    onClear,
    validationStatus,
    showSecrets,
    formData,
    handleInputChange = () => {},
    toggleSecretVisibility = () => {},
    handleSaveService = () => {},
  }: ApiKeyCardProps) {
  const status = forceStatus || validationStatus?.status || 'idle';
    const message = forceStatus ? undefined : validationStatus?.message; // Suppress old messages when forcing status
    const isValid = status === 'valid';
    const isSaving = status === 'saving';

    const getStatusIcon = (status: string) => {
        switch (status) {
          case 'saving':
            return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
          case 'valid':
            return <CheckCircle className="h-4 w-4 text-green-500" />;
          case 'invalid':
            return <XCircle className="h-4 w-4 text-red-500" />;
          default:
            return null;
        }
      };

      const getStatusBadge = (status: string, required: boolean = false) => {
          if (status === 'valid') {
            return <Badge variant="default" className="text-xs bg-green-100 text-green-800">Configured</Badge>;
          }
          if (status === 'invalid') {
            return <Badge variant="destructive" className="text-xs">Invalid</Badge>;
          }
          return required ? 
            <Badge variant="destructive" className="text-xs">Required</Badge> : 
            <Badge variant="secondary" className="text-xs">Optional</Badge>;
        };

    
    return (
      <Card className="relative" data-service={serviceKey}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon className="h-5 w-5 text-muted-foreground" />
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{title}</CardTitle>
                {getStatusIcon(status)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(status, required)}
            </div>
          </div>
          <CardDescription className="text-sm">{description}</CardDescription>
          {message && (
            <Alert className={`mt-2 ${status === 'valid' ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950' : status === 'invalid' ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
              <AlertDescription className={`text-xs ${status === 'valid' ? 'text-green-800 dark:text-green-200' : status === 'invalid' ? 'text-red-800 dark:text-red-200' : 'text-foreground'}`}>
                {message}
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {!hideFields && (
            <>
              {fields.map((field) => {
                const isSecret = field.type === "password";
                const shouldShowSecret = showSecrets[field.key];
                const displayType = isSecret && !shouldShowSecret ? "password" : "text";
                
                return (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={field.key} className="text-sm font-medium">
                      {field.label}
                    </Label>
                    <div className="relative">
                      <Input
                        id={field.key}
                        type={displayType}
                        placeholder={field.placeholder}
                        value={formData[field.key] || ""}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        className="text-sm pr-10"
                      />
                      {isSecret && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleSecretVisibility(field.key)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        >
                          {shouldShowSecret ? (
                            <EyeOff className="h-3 w-3" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                        </Button>
                      )}
                    </div>
                    {field.validation && (
                      <p className="text-xs text-muted-foreground">{field.validation}</p>
                    )}
                  </div>
                );
              })}
              
              <div className="flex items-center gap-2 pt-2">
                <Button 
                  onClick={() => handleSaveService(serviceKey)}
                  disabled={isSaving}
                  size="sm"
                  variant={isValid ? "outline" : "default"}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Saving...
                    </>
                  ) : isValid ? (
                    <>
                      <Check className="h-3 w-3 mr-1" />
                      Update
                    </>
                  ) : (
                    'Save Configuration'
                  )}
                </Button>
                
                {testAction && (
                  <Button onClick={testAction} variant="outline" size="sm">
                    Test
                  </Button>
                )}

                {showClearButton && (
                  <Button 
                    onClick={onClear}
                    variant="outline" 
                    size="sm"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                )}
                
                <Button asChild variant="ghost" size="sm">
                  <a href={docUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Docs
                  </a>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
}